import { z } from 'zod';
import { auditSchema, idSchema, isoDateTimeSchema } from '../../common.js';
export * from './scaling.js';
/** The cook log (`COOKS_DOC`) — its own family doc rather than a field on `Recipe`, so the
 * check-in feed can read cooks without subscribing to the whole recipe box. See its
 * docblock for the no-tallies rule that governs everything downstream of it. */
export * from './cooks.js';
/** Yjs doc holding `Y.Map<recipeId, Recipe>` (ROADMAP §1.3). */
export const RECIPES_DOC = 'recipes';
// ---------------------------------------------------------------------------------------
// Photos (WS-20) — mirrors the check-in module's shape (`packages/shared/src/modules/
// checkin/index.ts`) deliberately, right down to field names, so the client's outbox/store
// machinery generalizes instead of forking. One real difference: recipes have exactly one
// photo (the header image), not a feed of them, so there is no seen-by/announce plumbing
// here — just upload, replace, render.
// ---------------------------------------------------------------------------------------
/** Longest side the client downscales a capture to before encoding — same cap as check-in. */
export const RECIPE_PHOTO_MAX_DIMENSION = 1600;
/** JPEG quality used by the canvas re-encode (which is also what strips EXIF). */
export const RECIPE_PHOTO_JPEG_QUALITY = 0.8;
/** Server-side upload cap. A 1600px JPEG at q0.80 lands far below this. */
export const RECIPE_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
/** Content types the blob store accepts. */
export const RECIPE_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const recipePhotoContentTypeSchema = z.enum(RECIPE_PHOTO_CONTENT_TYPES);
/** Longest side of the server-generated card thumbnail. Recipe cards run wider than the
 * check-in feed's tiles, so this is bigger than `CHECKIN_THUMB_SIZE`. */
export const RECIPE_THUMB_SIZE = 480;
/** Longest side of the server-generated "display" variant — the detail page's hero image. */
export const RECIPE_DISPLAY_SIZE = 1200;
/** A photo id is the lowercase hex sha256 of the *encoded* bytes (content-addressed). */
export const recipePhotoIdSchema = z.string().regex(/^[0-9a-f]{64}$/, 'expected a sha256 hex id');
/**
 * Where a photo currently lives — identical state machine to check-in's `photoRef.state`:
 * `local` (this device's outbox only) → `uploading` → `uploaded` (server has the bytes).
 * The importer and the photo-backfill CLI both write photos server-side, so their results
 * start life already `uploaded` — there is no device waiting to push them.
 */
export const RECIPE_PHOTO_STATES = ['local', 'uploading', 'uploaded'];
export const recipePhotoStateSchema = z.enum(RECIPE_PHOTO_STATES);
export const recipePhotoRefSchema = z.object({
    id: recipePhotoIdSchema,
    /** Encoded pixel dimensions, so the header can reserve space before the bytes arrive. */
    w: z.number().int().min(1).max(20_000),
    h: z.number().int().min(1).max(20_000),
    state: recipePhotoStateSchema,
});
/** Transitions are forward-only, same rule as check-in's `canAdvancePhotoState`. */
export function canAdvanceRecipePhotoState(from, to) {
    if (from === to)
        return false;
    if (from === 'local')
        return to === 'uploading';
    if (from === 'uploading')
        return to === 'uploaded' || to === 'local';
    return false; // 'uploaded' is terminal
}
/** True when *any* client can fetch the bytes from the server. */
export function isRecipePhotoFetchable(ref) {
    return ref.state === 'uploaded';
}
/** Auth'd full-size photo URL. Same-origin path — never an absolute URL (north-star #7). */
export function recipePhotoUrl(id) {
    return `/api/recipes/photo/${id}`;
}
/** Auth'd ~480px thumbnail URL (recipe cards, the box grid). */
export function recipeThumbUrl(id) {
    return `/api/recipes/photo/${id}/thumb`;
}
/** Auth'd ~1200px "display" URL (the detail page's header). */
export function recipeDisplayUrl(id) {
    return `/api/recipes/photo/${id}/display`;
}
/** `PUT /api/recipes/photo/:id` response. `created: false` means the id was already stored. */
export const recipePhotoUploadResponseSchema = z.object({
    id: recipePhotoIdSchema,
    created: z.boolean(),
    bytes: z.number().int().min(0),
});
export const ingredientSchema = z.object({
    id: idSchema,
    /** Raw line as written/imported, e.g. "2 cups flour" — always preserved. */
    text: z.string().min(1).max(500),
    quantity: z.string().max(100).optional(),
    /**
     * Upper bound of a range ("2–3 cloves garlic"), when the line states one (WS-18).
     * A *string* like `quantity`, not the number the AI task emits — the stored ingredient
     * has always kept quantities as written text and `parseIngredientLine`/`scaling.ts`
     * re-parse on read. Optional, so every pre-WS-18 ingredient stays valid.
     */
    quantityMax: z.string().max(100).optional(),
    unit: z.string().max(50).optional(),
    name: z.string().max(300).optional(),
    /** Preparation split out of the name ("minced", "melted, cooled") — WS-18. */
    prep: z.string().max(200).optional(),
    /** "1 tsp vanilla (optional)" — WS-18. Absent means required. */
    optional: z.boolean().optional(),
});
export const recipeSchema = auditSchema.extend({
    id: idSchema,
    title: z.string().min(1).max(300),
    ingredients: z.array(ingredientSchema).max(200),
    steps: z.array(z.string().max(5000)).max(200),
    sourceUrl: z.string().url().optional(),
    tags: z.array(z.string().min(1).max(50)).max(50),
    notes: z.string().max(10_000).optional(),
    /**
     * The recipe's one header photo (WS-20) — mirrors check-in's `photoRef` shape exactly.
     * Absent means "no photo yet"; the card/detail render a pure, code-computed emoji-on-
     * gradient placeholder in that case (`placeholderArtFor`, `apps/web/src/modules/recipes/
     * utils/placeholderArt.ts`) — never stored, always derived, so it never goes stale and
     * costs zero model calls (owner call 2026-08-04: no token spend for a placeholder icon).
     */
    photoRef: recipePhotoRefSchema.optional(),
    servings: z.number().int().min(1).max(100).optional(),
    /**
     * Provenance marker for the WS-18 cleanup pass (`docs/AI-PIPELINE.md`). Set to `'ai'` by
     * the backfill client alongside the normal audit fields, so an AI-tidied recipe is always
     * distinguishable from one a human typed. Absent on everything written before WS-18.
     */
    cleanedBy: z.literal('ai').optional(),
    cleanedAt: isoDateTimeSchema.optional(),
});
/**
 * URL import (ROADMAP §2 Phase 1 Recipes). The server fetches + parses; the client always
 * does the actual Yjs write (ROADMAP §1.3 — REST never mutates collaborative state), so the
 * response only ever carries plain data for the client to drop into the manual editor or
 * write directly.
 */
export const recipeImportRequestSchema = z.object({
    url: z.string().url().max(2000),
});
/** A parsed-but-not-yet-saved recipe. Deliberately looser than `recipeSchema`: partial
 * extraction is expected and the client must still be able to save/edit it. */
export const importedRecipeSchema = z.object({
    title: z.string().max(300).optional(),
    ingredients: z.array(z.string().min(1).max(500)).max(200),
    steps: z.array(z.string().max(5000)).max(200),
    sourceUrl: z.string().url().optional(),
    tags: z.array(z.string().min(1).max(50)).max(50).optional(),
    notes: z.string().max(10_000).optional(),
    servings: z.number().int().min(1).max(100).optional(),
    /**
     * Set only when the importer already downloaded, re-encoded and stored the source page's
     * image server-side (WS-20) — arrives already `state: 'uploaded'`, since no device needs
     * to push these bytes. Absent when the page had no usable image or the download failed;
     * the form falls back to the code-computed placeholder like any other photo-less recipe.
     */
    photoRef: recipePhotoRefSchema.optional(),
});
export const recipeImportResponseSchema = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        recipe: importedRecipeSchema,
        /** e.g. "no steps found" — surfaced so the client can nudge toward a review, not block it. */
        warnings: z.array(z.string()).max(20).default([]),
    }),
    z.object({
        ok: z.literal(false),
        error: z.string().max(200),
        /** Whatever could be salvaged before parsing gave up — DESIGN-BRIEF §5: never block
         * manual entry, so a failed import still seeds the editor when it can. */
        partial: importedRecipeSchema.partial().optional(),
    }),
]);
//# sourceMappingURL=index.js.map